import { usePropsValue } from '@hooks/UsePropsValue'
import React, { isValidElement } from 'react'
import type { ReactNode, ReactElement } from 'react'
import { withNativeProps } from '@utils/NativeProps'
import List from '@components/list/src/index'
import classNames from 'classnames'
import { useShouldRender } from '@utils/ShouldRender'
import { traverseReactNode } from '@utils/TraverseReactNode'
import { AccordionPanelProps, AccordionProps } from './iAccordion'
import WebUI from '@utils/UpdatePrefixs'
import ArrowIosDown from '@tinper/m-icons/lib/cjs/ArrowIosDown'

export const AccordionPanel: React.FC<AccordionPanelProps> = () => null

const AccordionPanelContent: React.FC<{
  visible: boolean
  forceRender: boolean
  destroyOnClose: boolean
  children?: ReactNode
  clsPrefix?: string
  fieldid?: string
  contentKey?: string
  contentStyle?: React.CSSProperties
}> = props => {
  const { visible } = props
  const shouldRender = useShouldRender(
    visible,
    props.forceRender,
    props.destroyOnClose
  )

  return (
    <div
      className={classNames(`${props.clsPrefix}-panel-content`, { [`${props.clsPrefix}-panel-content-active`]: visible, })}
      style={{
        ...props.contentStyle,
        display: visible ? 'block' : 'none',
      }}
      fieldid={`${props.fieldid}-panel-content_${props.contentKey}`}
    >
      <div className={`${props.clsPrefix}-panel-content-inner`} fieldid={`${props.fieldid}-panel-content-inner_${props.contentKey}`}>
        <List.Item fieldid={`${props.fieldid}-panel-content-inner-listitem_${props.contentKey}`}>{shouldRender && props.children}</List.Item>
      </div>
    </div>
  )
}

const Accordion: React.FC<AccordionProps> = props => {
  const panels: ReactElement<AccordionPanelProps>[] = []

  const { prefix } = props;

  traverseReactNode(props.children, child => {
    if (!isValidElement<AccordionPanelProps>(child)) return
    const key = child.key
    if (typeof key !== 'string') return

    panels.push(child)
  })

  const handlePropsValue = () => {
    if (!props.accordion) {
      return {
        value: props.activeKey,
        defaultValue: props.defaultActiveKey ?? [],
        onChange: props.onChange,
      }
    }

    const initValue: {
      value?: any
      defaultValue: any
      onChange: (v: any) => void
    } = {
      value: [],
      defaultValue: [],
      onChange: v => {
        props.onChange?.(v[0] ?? null)
      },
    }

    if (props.activeKey === undefined) {
      initValue.value = undefined
    } else if (props.activeKey !== null) {
      initValue.value = [props.activeKey]
    }

    if (
      ![null, undefined].includes(props.defaultActiveKey as null | undefined)
    ) {
      initValue.defaultValue = [props.defaultActiveKey]
    }

    return initValue
  }

  const [activeKey, setActiveKey] = usePropsValue<string[]>(handlePropsValue())

  const activeKeyList =
    activeKey === null ? [] : Array.isArray(activeKey) ? activeKey : [activeKey]

  const _clsPrefix = `${props.clsPrefix}-accordion`

  const renderPrefix = (key: any) => {
    if (prefix) {
      let prefixNode;
      if (typeof prefix === 'function') {
        prefixNode = prefix(key) as ReactElement
      } else if (prefix === true) {
        prefixNode = <div className={`${_clsPrefix}-prefix-default`} />
      } else {
        prefixNode = prefix as ReactElement
      }
      return prefixNode
    }
  }

  return withNativeProps(
    props,
    <div className={_clsPrefix} fieldid={props.fieldid}>
      <List fieldid={`${props.fieldid}-list`}>
        {panels.map((panel, index) => {
          const key = panel.key as string
          const active = activeKeyList.includes(key)

          function handleClick(event: React.MouseEvent<Element, MouseEvent>) {
            if (props.accordion) {
              if (active) {
                setActiveKey([])
              } else {
                setActiveKey([key])
              }
            } else {
              if (active) {
                setActiveKey(activeKeyList.filter(v => v !== key))
              } else {
                setActiveKey([...activeKeyList, key])
              }
            }

            panel.props.onClick?.(event)
          }

          const renderArrow = (key: any) => {
            let arrow: AccordionProps['arrow'] = <ArrowIosDown fieldid={`${props.fieldid}-arrow-icon_${key}`}/>
            if (props.arrow !== undefined) {
              arrow = props.arrow
            }
            if (panel.props.arrow !== undefined) {
              arrow = panel.props.arrow
            }
            return typeof arrow === 'function' ? (
              arrow(active)
            ) : (
              <div
                className={classNames(`${_clsPrefix}-arrow`, { [`${_clsPrefix}-arrow-active`]: active, })}
                fieldid={`${props.fieldid}-panel-arrow_${key}`}
              >
                {arrow}
              </div>
            )
          }
          const renderLine = () => index == panels?.length - 1 ? null : (
            <div className={`${_clsPrefix}-line`} fieldid={`${props.fieldid}-line_${key}`} />
          )
          const listItemCls = (key: any) => {
            if (activeKey?.length > 0 && activeKey.includes(key)) {
              return `${_clsPrefix}-panel-header-active`
            } else {
              return `${_clsPrefix}-panel-header`
            }
          }
          return (
            <React.Fragment key={key}>
              {withNativeProps(
                panel.props,
                <List.Item
                  className={listItemCls(key)}
                  onClick={handleClick}
                  disabled={panel.props.disabled}
                  prefix={renderPrefix(key)}
                  arrow={renderArrow(key)}
                  fieldid={`${props.fieldid}-panel_${key}`}
                >
                  {panel.props.title}
                </List.Item>
              )}
              <AccordionPanelContent
                visible={active}
                contentKey={key}
                forceRender={!!panel.props.forceRender}
                destroyOnClose={!!panel.props.destroyOnClose}
                fieldid={props.fieldid}
                clsPrefix={_clsPrefix}
                contentStyle={panel.props.contentStyle}
              >
                {panel.props.children}
              </AccordionPanelContent>
              {renderLine()}
            </React.Fragment>
          )
        })}
      </List>
    </div>
  )
}

export default WebUI({ })(Accordion)
